// SecondaryNavigation.tsx
import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component'
import ContentWrapper, { ContentColumn } from '@/layouts/ContentWrapper/ContentWrapper.component'
import { IProps, IMenuItem } from './SecondaryNavigation.interface'
import Button from '@/elements/Button/Button.component'
import Icon from '@/elements/Icon/Icon.component'
import '@/blocks/SecondaryNavigation/SecondaryNavigation.styles.css'

const SecondaryNavigation: React.FC<IProps> = ({
    backLink,
    items,
    className,
    ...blockWrapperProps
}) => {
    // keep a local copy so we can toggle open/closed
    const [navItems, setNavItems] = useState<IMenuItem[]>(items)

    // if the consumer ever passes new items, re-sync
    useEffect(() => {
        setNavItems(items)
    }, [items])

    // recursively flip `isExpanded` at the given path
    const toggleItemAtPath = (
        list: IMenuItem[],
        path: number[]
    ): IMenuItem[] => {
        if (path.length === 0) return list
        const [head, ...rest] = path
        return list.map((item, i) => {
            if (i !== head) return item
            if (rest.length === 0) {
                return { ...item, isExpanded: !item.isExpanded }
            }
            return {
                ...item,
                subMenu: item.subMenu
                    ? toggleItemAtPath(item.subMenu, rest)
                    : item.subMenu,
            }
        })
    }

    const handleToggle = (path: number[]) => {
        setNavItems(prev => toggleItemAtPath(prev, path))
    }

    /**
     * @param menuItems array of items at this level
     * @param level      nesting level (1..3)
     * @param path       array of indices from the root to here
     */
    const renderMenu = (
        menuItems: IMenuItem[],
        level = 1,
        path: number[] = []
    ) => {
        // only support 3 levels
        if (level > 3) return null

        // build an id for this <ul> so buttons can aria-control it
        const ulId =
            path.length > 0
                ? `sec-nav-level-${level}-${path.join('-')}`
                : undefined

        return (
            <ul
                className={clsx(`secondaryNavigation__level--${level}`)}
                {...(ulId ? { id: ulId } : {})}
            >
                {menuItems.map((item, idx) => {
                    const currentPath = [...path, idx]
                    const hasChildren = !!(
                        item.subMenu && item.subMenu.length > 0
                    )
                    const childUlId = `sec-nav-level-${level + 1}-${currentPath.join(
                        '-'
                    )}`

                    return (
                        <li
                            key={currentPath.join('-')}
                            className={clsx('secondaryNavigation__item', {
                                'secondaryNavigation__item--expanded': item.isExpanded,
                                'secondaryNavigation__item--selected': item.isSelected,
                            })}
                        >
                            <div className={clsx(`secondaryNavigation__linkWrapper secondaryNavigation__linkWrapper-${level}`, {
                                'secondaryNavigation__linkWrapper--expanded': item.isExpanded,
                            })}>
                                <a
                                    href={item.menu.href}
                                    aria-label={item.menu.ariaLabel}
                                    target={item.menu.target}
                                    title={item.menu.title}
                                    className={clsx('secondaryNavigation__link', {
                                        'secondaryNavigation__link--selected':
                                            item.isSelected,
                                    })}
                                    {...(item.isSelected
                                        ? { 'aria-current': 'page' }
                                        : {})}
                                >
                                    {item.menu.text}
                                </a>

                                {hasChildren && (
                                    <Button
                                        as="button"
                                        size="sm"
                                        variant="secondary"
                                        className="secondaryNavigation__toggle"
                                        aria-expanded={item.isExpanded}
                                        aria-controls={childUlId}
                                        aria-label={
                                            item.isExpanded
                                                ? `Collapse submenu for ${item.menu.text}`
                                                : `Expand submenu for ${item.menu.text}`
                                        }
                                        onClick={() => handleToggle(currentPath)}
                                    >
                                        <Icon
                                            icon={
                                                'chevron-down'
                                            }
                                            className="secondaryNavigation__chevron"
                                            aria-hidden="true"
                                        />
                                    </Button>
                                )}
                            </div>

                            {item.isExpanded &&
                                hasChildren &&
                                renderMenu(item.subMenu!, level + 1, currentPath)}
                        </li>
                    )
                })}
            </ul>
        )
    }

    return (
        <BlockWrapper
            className={clsx('secondaryNavigation', className)}
            {...blockWrapperProps}
        >
            <nav
                aria-label="Secondary navigation"
                className="secondaryNavigation__nav"
            >
                <ContentWrapper>
                    <ContentColumn>
                        {backLink && (

                            <Button
                                as="a"
                                href={backLink.href}
                                aria-label={backLink.ariaLabel}
                                variant="secondary"
                                size="sm"
                                icon="chevron-left"
                                iconPosition="before"
                                className="secondaryNavigation__backButton"
                            >
                                {backLink.text}
                            </Button>

                        )}
                        {renderMenu(navItems)}
                    </ContentColumn>
                </ContentWrapper>
            </nav>
        </BlockWrapper>
    )
}

export { SecondaryNavigation }
